import { useState, useEffect, useRef, useCallback } from "react";
import config from "../../config";
import { useLanguage } from "../../Context";

export default function Minibook() {
  const [contents, setContents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [latestMinibooks, setLatestMinibooks] = useState([]);
  const [isLatestLoading, setIsLatestLoading] = useState(true);

  const { language } = useLanguage();
  const loaderRef = useRef(null);

  // Fetch latest 4 minibooks
  useEffect(() => {
    setIsLatestLoading(true);
    fetch(
      `${config.apiUrl}contentapi.php?option=1&type=7&categoryid=7&subcategory=0&limitCount=4&lang=${language}`
    )
      .then((response) => response.json())
      .then((latestPublication) => {
        setLatestMinibooks(latestPublication);
        setIsLatestLoading(false);
      })
      .catch((error) => {
        setIsLatestLoading(false);
        console.error("Error fetching latest minibooks:", error);
      });
  }, [language]);

  // Fetch paginated minibooks
  const fetchMinibooks = useCallback(
    async (reset = false) => {
      if (!hasMore && !reset) return;
      if (reset) {
        setIsLoading(true);
        setPage(1);
      } else {
        setIsLoadingMore(true);
      }
      try {
        const response = await fetch(
          `${
            config.apiUrl
          }lazyLoad.php?option=2&categoryid=7&subcategory=0&skip=4&page=${
            reset ? 1 : page
          }&lang=${language}`
        );
        const data = await response.json();
        const newContents = data.map((item) => ({
          id: item.id,
          categoryE: item.categoryE,
          FileUrl: item.FileUrl,
          ImageUrl: item.ImageUrl,
          PostDate: item.PostDate,
          ShortDesc: item.ShortDesc,
          SubCategory: item.SubCategory,
          SubCategoryd: item.SubCategoryd,
          Title: item.Title,
          contentViews: item.contentViews,
          fulldesc: item.fulldesc,
        }));

        if (reset) {
          setContents(newContents);
          setHasMore(newContents.length > 0);
          setPage(2);
        } else {
          setContents((prev) => [...prev, ...newContents]);
          setHasMore(newContents.length > 0);
          setPage((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Error fetching minibooks:", error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [page, language, hasMore]
  );

  // Reset and fetch on language change
  useEffect(() => {
    setContents([]);
    setHasMore(true);
    setPage(1);
    fetchMinibooks(true);
    // eslint-disable-next-line
  }, [language]);

  // Infinite scroll using IntersectionObserver
  useEffect(() => {
    if (!hasMore) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && !isLoadingMore) {
          fetchMinibooks();
        }
      },
      { threshold: 1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
    // eslint-disable-next-line
  }, [loaderRef, hasMore, isLoading, isLoadingMore, fetchMinibooks]);

  const styles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(15rem, 2fr))",
    gap: "1rem",
  };

  return (
    <div className="bg-body">
      <div className="max-container-width py-3 text-body">
        {/* Latest Minibooks */}
        <div className="four-latest-publication mb-4">
          {isLatestLoading
            ? [...Array(4)].map((_, index) => (
                <div key={index} className="latest-publication-even">
                  <div
                    className="bg-body-tertiary"
                    style={{ minHeight: "38vh" }}
                  />
                  <div className="p-3">
                    <div className="placeholder-glow">
                      <span
                        className="placeholder col-6 mb-3"
                        style={{ height: "1.8rem" }}
                      />
                      <span className="placeholder col-12 mb-3" />
                      <span className="placeholder col-12 mb-3" />
                      <span className="placeholder col-12 mb-3" />
                      <span className="placeholder col-8 mb-3" />
                    </div>
                  </div>
                </div>
              ))
            : latestMinibooks.map((latestItems) => (
                <div
                  key={latestItems.id}
                  className="card bg-body-secondary border-0 rounded-1 p-3 latest-publication-even flex-grow-1"
                >
                  <a
                    href={`/minibook/${latestItems.SubCategory}/${latestItems.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={latestItems.ImageUrl?.replace(
                        /_large(?=\.(jpg|jpeg|png)$)/,
                        "_thumb"
                      )}
                      className="card-img-top"
                      alt="thumbnail"
                      style={{
                        height: "100%",
                        width: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </a>
                  <div className="card-body p-1">
                    <h3
                      className="card-text fs-5 border-end border-5 pe-2 card-text mb-1 text-truncate p-1"
                      id="secondary-border-yellow"
                    >
                      {latestItems.Title}
                    </h3>
                    <p className="text-break limit-text">
                      {latestItems.ShortDesc}
                    </p>
                    <a
                      href={`/minibook/${latestItems.SubCategory}/${latestItems.id}`}
                      rel="noreferrer"
                      target="_blank"
                      className="text-dark text-decoration-underline"
                    >
                      مطالعه کتاب&gt;&gt;
                    </a>
                  </div>
                </div>
              ))}
        </div>

        {/* All Minibooks */}
        <div style={styles} className="mb-4">
          {contents.map((minibook) => (
            <div
              key={minibook.id}
              className="card bg-body-secondary border-0 rounded-1 p-2"
            >
              <a
                href={`/minibook/${minibook.SubCategory}/${minibook.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={minibook.ImageUrl?.replace(
                    /_large(?=\.(jpg|jpeg|png)$)/,
                    "_thumb"
                  )}
                  className="card-img-top"
                  alt="thumbnail"
                  style={{ width: "100%", objectFit: "cover" }}
                />
              </a>
              <div className="card-body p-1 text-center">
                <h4 className="card-text fs-6 mb-1 p-1" id="publication-title">
                  {minibook.Title}
                </h4>
                <p className="text-break limit-text fs-6">
                  {minibook.ShortDesc}
                </p>
                <a
                  href={`/minibook/${minibook.SubCategory}/${minibook.id}`}
                  rel="noreferrer"
                  target="_blank"
                  className="btn yellow-btn-hover px-4 text-dark secondary-bg-yellow border-0 rounded-1"
                >
                  مطالعه کتاب
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Loader for infinite scroll (always rendered for observer) */}
        <div ref={loaderRef}>
          {(isLoadingMore || isLoading) && (
            <div className="placeholder-glow mb-4" style={styles}>
              {[...Array(5)].map((_, index) => (
                <div key={index} className="bg-body-secondary">
                  <div
                    className="w-100 placeholder"
                    style={{ height: "18rem" }}
                  />
                  <div className="p-3 text-center">
                    <div className="col-12 placeholder mb-3" />
                    <div className="col-6 placeholder btn" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!hasMore && !isLoading && (
          <div className="mt-5 text-center">
            <small className="p-2 bg-secondary text-light rounded">
              {parseInt(language) === 1 ? "پایان مطالب" : "د محتوا پای"}
            </small>
          </div>
        )}
      </div>
    </div>
  );
}
